import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import VideoCard from '@/components/base/VideoCard';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { videos } from '@/mocks/videos';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="container-custom py-20 text-center">
            <div className="w-24 h-24 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-error-warning-line text-4xl text-foreground-400"></i>
            </div>
            <h1 className="text-2xl font-heading text-foreground-950 mb-3">视频未找到</h1>
            <p className="text-foreground-500 mb-6">您要查看的视频不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedVideosList = article.relatedVideos
    ? videos.filter((v) => article.relatedVideos.includes(v.id))
    : [];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.description,
        author: {
          '@type': 'Organization',
          name: article.author,
        },
        datePublished: article.publishDate,
        image: article.coverImage,
        articleBody: article.content.replace(/<[^>]*>/g, ''),
        url: `https://fulishipin.com/article/${article.slug}`,
        publisher: {
          '@type': 'Organization',
          name: '福利视频在线',
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '首页',
            item: 'https://fulishipin.com',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: category?.name || article.category,
            item: `https://fulishipin.com/category/${article.category}`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: article.title,
          },
        ],
      },
    ],
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: category?.name || article.category, href: `/category/${article.category}` },
    { label: article.title },
  ];

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200/60">
            <div className="container-custom">
              <Breadcrumb items={breadcrumbItems} />
            </div>
          </div>

          <article className="container-custom py-8">
            <div className="max-w-[860px] mx-auto">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">
                  {category?.name || article.category}
                </span>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 rounded-full text-xs bg-background-200 text-foreground-600">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-4xl font-heading text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
                <span>{article.author}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span>{article.publishDate}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <div className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  <span>{(article.views / 10000).toFixed(1)}万 次观看</span>
                </div>
              </div>

              <div className="rounded-lg overflow-hidden mb-8">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  title={article.title}
                  className="w-full aspect-video object-cover"
                />
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
                  [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-heading [&_h2]:text-foreground-950 [&_h2]:mt-8 [&_h2]:mb-4
                  [&_h3]:text-lg [&_h3]:font-heading [&_h3]:text-foreground-900 [&_h3]:mt-6 [&_h3]:mb-3
                  [&_p]:mb-4 [&_p]:text-foreground-700 [&_p]:leading-relaxed
                  [&_strong]:text-foreground-900"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {relatedVideosList.length > 0 && (
                <div className="mt-12 pt-8 border-t border-background-200/60">
                  <h3 className="text-xl font-heading text-foreground-950 mb-6">
                    相关推荐
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {relatedVideosList.map((video) => (
                      <VideoCard key={video.id} video={video} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}